import moment from 'moment'
import { FC, ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { DOMContainer } from 'react-overlays/esm/useWaitForDOMRef'
import { Link, useParams } from 'react-router-dom'
import { ReactComponent as Back } from '../../assets/icons/back.svg'
import AppliedRule from '../../components/appliedRule'
import CardExamList from '../../components/cardExamList'
import H3 from '../../components/h3'
import Skeleton from '../../components/skeleton'
import { usePatients } from '../../hooks/patients'
import { RoutesEnum } from '../../utils/routesEnum'
import ExamHistoryOverlay from './examHistoryOverlay'
import { ContainerExam, ListContainer, Patient, PatientInfo, PatientInfoViewer } from './style'

type PatientViewProps = {
  setNavigationComponent: (re: ReactElement | undefined) => void;
}

const sexType = {
  male: 'Masculino',
  female: 'Feminino'
}

const PatientView: FC<PatientViewProps> = ({ setNavigationComponent }): ReactElement | null => {
  const [isLoading, setIsLoading] = useState(false)
  const { getOne, patient, processedExam, getLastProcessedExam, clearLastProcessedExam } = usePatients()
  const { patientId } = useParams<{ patientId: string }>()
  const elementRef = useRef<DOMContainer<HTMLElement> | null>(null)
  const [isOpenOverlay, setIsOpenOverlay] = useState('')

  useLayoutEffect(() => {
    document.body.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }, [])

  useEffect(() => {
    return () => clearLastProcessedExam()
  }, [])

  useMemo(() => {
    setIsLoading(true)
    getOne(patientId).finally(() => setIsLoading(false))
    getLastProcessedExam(patientId)
  }, [patientId, getLastProcessedExam, setIsLoading])

  const onClickExam = useCallback((examId: string, e: { target: DOMContainer<HTMLElement> }) => {
    elementRef.current = e.target
    setIsOpenOverlay(examId)
  }, [])

  useEffect(() => {
    if (processedExam?.extracted?.length) {
      setNavigationComponent(
        <CardExamList
          exams={processedExam?.extracted || []}
          onClickExam={onClickExam}
        />
      )
    } else {
      setNavigationComponent(undefined)
    }

    return () => setNavigationComponent(undefined)
  }, [processedExam, setNavigationComponent])

  return (
    <ListContainer>
      <Patient>
        <Link to={RoutesEnum.PATIENTS}>
          <Back />
        </Link>
        <H3>
          <Skeleton visible={isLoading}>
            {patient?.name || 'Sem nome'}
          </Skeleton>
        </H3>
        <Link className='edit' to={RoutesEnum.PATIENTS_EDIT.replace(':patientId', patientId)}>
          Editar Paciente
        </Link>
      </Patient>
      <PatientInfoViewer>
        <PatientInfo>
          <span>Idade</span>
          <Skeleton visible={isLoading}>
            {patient?.age || 0}
          </Skeleton>
        </PatientInfo>
        <PatientInfo>
          <span>Sexo do paciente</span>
          <Skeleton visible={isLoading}>
            {patient?.sex ? sexType[patient?.sex] : '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'}
          </Skeleton>
        </PatientInfo>
        <PatientInfo>
          <span>IMC</span>
          <Skeleton visible={isLoading}>
            {patient?.imc || 0}
          </Skeleton>
        </PatientInfo>
        <PatientInfo>
          <span>Processamentos</span>
          <Skeleton visible={isLoading}>
            {patient?.totalAnalysedExams || 0}
          </Skeleton>
        </PatientInfo>
      </PatientInfoViewer>

      {!!patient?.lastAnalysedExam && (
        <PatientInfo>
          <span>Último processamento</span>
          {moment(patient?.lastAnalysedExam).format('DD/MM/YYYY')}
        </PatientInfo>
      )}

      <ContainerExam>
        {processedExam?.result?.map(result => (
          <AppliedRule
            key={`result-${result._id}`}
            code={result.code}
            name={result.name}
            content={result.content}
            rules={result.rules}
            calculatedExams={result.calculatedExams}
            type='applied'
          />
        ))}
      </ContainerExam>
      {!!isOpenOverlay && (
        <ExamHistoryOverlay
          target={elementRef.current}
          patientId={patientId}
          examId={isOpenOverlay}
          onHide={() => setIsOpenOverlay('')}
        />
      )}
    </ListContainer>
  )
}

export default PatientView
